import {
    React,
    Box,
    Grid2,
    useState,
    useEffect,
    IconButton,
    Typography,
    CustomButton,
    AppLayout,
    CustomDropdown,
    CustomMultiSelectDropdown,
    useRef,
    CustomForecast,
    Dayjs,
    dayjs,
    Paper,
} from "../../utils/globalImports";

import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Tooltip from "@mui/material/Tooltip"; // ✅ Tooltip import
import { IFSHistoryType, IFSType } from "../../constants/constants";
import useApi from "../../hooks/useApi";
import CustomIFSTracker from "./CustomIFSTracker";
import { IFSGraphData, IFSGraphTotals, IFSProject } from "../../interfaces/IFSForecast.interfaces";
import Graph from "./Graph";
import CustomDropdownNumber from "../../components/dropdown/CustomDropdownNumber";
import ChangePeriodGraph from "./ChangePeriodGraph";
import { headingStyles } from "../../assets/styles/CommonStyles";
import { FilterListOutlined } from "@mui/icons-material";

interface CustomForecastRef {
    triggerSave: () => void;
}

interface IFSProjectResult {
    projects: IFSProject[];
    monthlySummaries?: IFSGraphData[]; // optional, can refine type later
    //fourMonthProposedTotal?: number;
    totals?: IFSGraphTotals;
}
interface IHistoryWeek {
    weekId: string;
    weekLabel: string;
}
interface ActiveMonthDetails{
  year:number,
  month:number,
  currentLockDate:Date,
  minAllowedDate:Date,
  maxAllowedDate:Date
}

const IFSTracker: React.FC = () => {
    const [filters, setFilters] = useState<any>({
        projectManagers: [],
        technicalProjectLeads: [],
        feeStages: [],
        tags: [],
        clients: [],
        projects: [],
    });

    const DEFAULT_DROPDOWN_WIDTH = 250;
    const DEFAULT_Single_DROPDOWN = true;
    const { execute, loading, error } = useApi();
    const forecastRef = useRef<CustomForecastRef>(null);

    // Tab state
    const [activeTab, setActiveTab] = useState<IFSType>(IFSType.IFSDefinite);

    // 🔹 Multi-select states
    const [projectManager, setProjectManager] = useState<(string | number)[]>([]);
    const [technicalLead, setTechnicalLead] = useState<(string | number)[]>([]);
    const [feeStage, setFeeStage] = useState<(string | number)[]>([]);
    const [tag, setTag] = useState<(string | number)[]>([]);
    const [client, setClient] = useState<(string | number)[]>([]);

    // 🔹 Single-select states
    const [status, setStatus] = useState<string | number>("-1");
    const [afp, setAfp] = useState<string | number>("-1");
    const [completedByIndia, setCompletedByIndia] = useState<string | number>("-1");

    // 🔹 IFS History specific filters
    const [selectedProjects, setSelectedProjects] = useState<(string | number)[]>([]);
    const [selectedWeek, setSelectedWeek] = useState<string | number>("-1");
    const [selectedType, setSelectedType] = useState<string | number>("-1");
    const [historyWeeks, setHistoryWeeks] = useState<IHistoryWeek[]>([]);

    const [showMore, setShowMore] = useState(false);
    const [financialYear, setFinancialYear] = useState<number>(() => {
        const today = dayjs();
        return today.month() >= 3 ? today.year() : today.year() - 1;
    });
    type GraphFilterMode = "FY" | "MONTH";

    const [graphFilterMode, setGraphFilterMode] =
        useState<GraphFilterMode>("FY");

    const [monthRange, setMonthRange] = useState<number | "">("");

    // Data state
    const [tableData, setTableData] = useState<IFSProject[]>([]);
    const [monthlySummaries, setMonthlySummaries] = useState<IFSGraphData[]>([]);
    const [localLoading, setLocalLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [isCompareEligible, setIsCompareEligible] = useState(false);
    //const [fourMonthProposedTotal, setFourMonthProposedTotal] = useState<number>(0);
    const [graphTotals, setGraphTotals] = useState<IFSGraphTotals | null>(null);
    const [isCompareClicked, setIsCompareClicked] = useState(false);
     const [activeMonthDetails,setActiveMonthDetails]=useState<ActiveMonthDetails|null>(null);
    const [fyRange, setFyRange] = useState<{
        min: number;
        max: number;
    } | null>(null);

    const EllipsisText = ({ text }: { text: string }) => (
        <Tooltip title={text} arrow>
            <span
                style={{
                    display: "inline-block",
                    maxWidth: "180px", // controls text width inside dropdown
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    verticalAlign: "middle",
                }}
            >
                {text}
            </span>
        </Tooltip>
    );

    const EXTRA_YEARS = 5;
    const financialYearOptions =
        fyRange
            ? Array.from(
                //{ length: fyRange.max - fyRange.min },
                { length: (fyRange.max + EXTRA_YEARS) - fyRange.min },
                (_, i) => {
                    const fyStart = fyRange.min + i;
                    return {
                        value: fyStart,
                        text: `Apr ${fyStart} - Mar ${fyStart + 1}`
                    };
                }
            )
            : [];


    const generateWeeksTillCurrent = () => {
        const today = dayjs();
        //const today = dayjs("2025-12-29");
        const currentMonth = today.month();
        const startOfCurrentWeek = today.startOf("week"); // Sunday

        const weeks: { weekId: string; weekLabel: string }[] = [];

        let currentStart = startOfCurrentWeek;
        let includedPrevMonthSunday = false;

        while (true) {
            const weekStart = currentStart;
            //const weekEnd = currentStart.add(6, "day");

            // Same month → always include
            if (weekStart.month() === currentMonth) {
                weeks.push({
                    weekId: weekStart.format("YYYY-MM-DD"),
                    weekLabel: `Week (${weekStart.format("DD-MM-YYYY")})`,
                });
            }
            // First Sunday of previous month → include once
            else if (!includedPrevMonthSunday) {
                weeks.push({
                    weekId: weekStart.format("YYYY-MM-DD"),
                    weekLabel: `Week (${weekStart.format("DD-MM-YYYY")})`,
                });
                includedPrevMonthSunday = true;
                break;
            } else {
                break;
            }

            currentStart = currentStart.subtract(1, "week");
        }

        return weeks.reverse().map((w, i) => ({
            ...w,
            weekLabel: `Week ${i + 1} ${w.weekLabel}`,
        }));
    };

    const dynamicWeeks = generateWeeksTillCurrent();
    const fetchActiveMonthDetails = async () => {
    

    try {
      const {data} = await execute<ActiveMonthDetails>(
        "get",
        `BaselineConfig/GetActiveMonthDetails`
      );
      setActiveMonthDetails(data);
    } catch (error) {
    //   setToast({ open: true, message: "Failed to fetch config dates", type: "error" })
    }
  };
    // ---------- Fetch filters ----------
    useEffect(() => {
        fetchActiveMonthDetails();
        const fetchFilters = async () => {
            const { data } = await execute("get", "/IFS/filters");

            if (data) setFilters(data);
        };
        fetchFilters();
    }, []);

    useEffect(() => {
        fetchWeeksByProjects(selectedProjects);
    }, [selectedProjects]);

    const fetchWeeksByProjects = async (projects: (string | number)[]) => {
        if (!projects.length) {
            setHistoryWeeks([]);
            return;
        }

        const { data } = await execute<IHistoryWeek[]>(
            "post",
            "/IFS/GetProjectWeeks",
            projects
        );

        if (data) {
            const sortedWeeks = [...data]
                .sort(
                    (a, b) =>
                        dayjs(a.weekId).valueOf() - dayjs(b.weekId).valueOf()
                )
                .map(w => ({
                    ...w,
                    weekId: dayjs(w.weekId).format("YYYY-MM-DD")
                }));

            setHistoryWeeks(sortedWeeks);
        }
    };

    // ---------- Build payload ----------
    const buildPayload = (ignoreFilters = false) => {
        const payload: any = {
            displayType: activeTab,
            financialYear,
        };

        // 🔹 Always include common filters (unless explicitly ignored)
        if (!ignoreFilters) {
            payload.projectManager =
                projectManager.length ? projectManager : null;

            payload.technicalProjectLead =
                technicalLead.length ? technicalLead : null;

            payload.feeStage =
                feeStage.length ? feeStage : null;

            payload.tag =
                tag.length ? tag : null;

            payload.client =
                client.length ? client : null;

            payload.afp =
                afp === "-1" ? null : afp === "1";

            payload.completedByIndia =
                completedByIndia === "-1" ? null : completedByIndia === "1";

            payload.status =
                status === "-1" ? null : status === "1";
        }

        // 🔹 IFS History specific filters
        if (activeTab === IFSType.IFSHistory) {
            payload.projects =
                selectedProjects.length ? selectedProjects : null;

            payload.week =
                selectedWeek === "-1" ? null : selectedWeek;

            payload.type =
                selectedType === "-1" ? null : selectedType;
        }

        // 🔹 Graph specific filters
        if (activeTab === IFSType.Graph) {
            payload.year = financialYear;
            payload.monthRange = monthRange || null;
        }
        // Change Period → ALWAYS send both
        if (activeTab === IFSType.ChangePeriod) {
            payload.year = financialYear;
            payload.monthRange = monthRange || 3; // default 3
        }
        return payload;
    };


    const fetchIdRef = useRef(0); // Track the latest request
    // ---------- Fetch data ----------
    const fetchData = async (ignoreFilters = false) => {
        if (!activeTab) return;
        const fetchId = ++fetchIdRef.current; // increment for every request
        setLocalLoading(true);
        try {
            const { data } = await execute<IFSProjectResult>(
                "post",
                "/IFS/GetIFSForecast",
                buildPayload(ignoreFilters)
            );
            // Only update state if this is the latest fetch
            if (fetchIdRef.current !== fetchId) return;
            if (activeTab === IFSType.Graph || activeTab === IFSType.ChangePeriod) {
                // 🔹 Save summary data (monthlySummaries) instead of projects
                const summaries = data?.monthlySummaries || [];
                setMonthlySummaries(summaries);

                //setFourMonthProposedTotal(data?.fourMonthProposedTotal ?? 0);
                setGraphTotals(data?.totals ?? null);
                if (!fyRange && summaries.length > 0) {
                    setFyRange({
                        min: Math.min(...summaries.map(m => m.earliestYear)),
                        max: Math.max(...summaries.map(m => m.latestYear))
                    });
                }
            } else {
                setTableData(data?.projects || []);
            }

        }

        catch (err) {
            console.error(err);
        } finally {
            // Stop loading after data is set
            if (fetchIdRef.current === fetchId) setLocalLoading(false);
        }
    };

    // ---------- Auto fetch on tab change ----------
    useEffect(() => {
        fetchData(true);
    }, [activeTab, financialYear, monthRange]);

    // ---------- Tab click ----------
    const handleTabClick = (tab: IFSType) => {
        setActiveTab(tab);
        handleClearFilters();
        setIsCompareEligible(false);
        setIsCompareClicked(false);
        setSelectedProjects([]);
        setSelectedWeek("-1");
        if (tab === IFSType.ChangePeriod) {
            setMonthRange(3);          // ✅ default 3 months
            setGraphFilterMode("MONTH");
        }
    };


    // ---------- Manual search ----------
    const handleSearch = () => {
        setIsCompareEligible(false);
        setIsCompareClicked(false);
        fetchData(false);
    };
    const handleCompare = () => {
        setIsCompareEligible(true);
        setIsCompareClicked(true);
        fetchData(false);
    };

    const handleFinancialYearChange = (e: any) => {
        const year = Number(e.target.value);
        setFinancialYear(year);
        setMonthRange("");          // reset month filter
        setIsCompareClicked(false);
    };

    const handleMonthRangeChange = (e: any) => {
        const value = e.target.value ? Number(e.target.value) : "";
        setMonthRange(value);
        setGraphFilterMode("MONTH");       // enable month, disable FY
    };


    const handleDownload = async () => {
        setDownloading(true); // start downloading
        try {
            const { data, error } = await execute<Blob>(
                "post",
                "/IFS/ExportIFSProjects",
                buildPayload(false),
                "blob"   // 👈 request as blob
            );

            if (error || !data) {
                console.error("Download failed:", error);
                return;
            }

            // Turn into Blob
            const blob = new Blob([data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = window.URL.createObjectURL(blob);

            // Download link
            const link = document.createElement("a");
            link.href = url;

            // Default filename (or parse from headers later)
            const fileName = `IFSProjects_${dayjs().format("YYYYMMDDHHmmss")}.xlsx`;
            link.setAttribute("download", fileName);

            document.body.appendChild(link);
            link.click();
            link.remove();

            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Download error:", err);
        } finally {
            setDownloading(false); // reset after download
        }
    };


    // ---------- Reset filters ----------
    const isAnyFilterActive =
        activeTab === IFSType.Graph
            ? monthRange !== ""
            : projectManager.length > 0 ||
            technicalLead.length > 0 ||
            feeStage.length > 0 ||
            tag.length > 0 ||
            client.length > 0 ||
            afp !== "-1" ||
            completedByIndia !== "-1" ||
            status !== "-1";

    const handleClearFilters = () => {

        setProjectManager([]);
        setTechnicalLead([]);
        setFeeStage([]);
        setTag([]);
        setClient([]);
        setAfp("-1");
        setCompletedByIndia("-1");
        setStatus("-1");
        if (activeTab === IFSType.Graph) {
            setMonthRange("");           // ✅ clear month dropdown
            setGraphFilterMode("FY");
            // optional: reset FY if needed
            // setFinancialYear(defaultFY);
        }
        fetchData(true);
    };
    const isHistorySearchEnabled =
        selectedProjects.length > 0 &&
        selectedWeek !== "-1" &&
        selectedType !== "-1";

    // ---------- Tooltip helpers ----------
    const buildTooltip = (
        selected: (string | number)[],
        options: any[],
        key: string,
        label: string
    ) => {
        const text = selected
            .map((id) => {
                const opt = options.find((o: any) => o[key] === id);
                return opt ? opt[label] : null;
            })
            .filter(Boolean)
            .join(", ");
        return text || "";
    };

    const withTooltip = (text: string) => (
        <Tooltip title={text}>
            <span
                style={{
                    display: "inline-block",
                    maxWidth: "180px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    verticalAlign: "bottom",
                }}
            >
                {text}
            </span>
        </Tooltip>
    );


    // const earliestYear = Math.min(...monthlySummaries.map(m => m.earliestYear));
    // const latestYear = Math.max(...monthlySummaries.map(m => m.latestYear));

    // const years = Array.from(
    //   { length: latestYear - earliestYear + 1 },
    //   (_, i) => earliestYear + i
    // );
    return (
        <AppLayout>
            <Box sx={{
                height: "100%", // 140px = approximate height of your Header + Footer
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
            }}>


                {/* ---------- Page Heading ---------- */}
                <Typography
                    sx={headingStyles}
                >
                    Income Forecast
                </Typography>

                {/* ---------- Tab Buttons ---------- */}
                <Grid2 container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Grid2>
                        <CustomButton
                            onClick={() => handleTabClick(IFSType.IFSDefinite)}
                            backgroundColor={
                                activeTab === IFSType.IFSDefinite ? "#1e66ab" : "#f0f0f0"
                            }
                            sx={{ color: activeTab === IFSType.IFSDefinite ? "#fff" : "#1e66ab" }}
                        >
                            IFS Definite
                        </CustomButton>
                    </Grid2>
                    <Grid2>
                        <CustomButton
                            onClick={() => handleTabClick(IFSType.IFSProposed)}
                            backgroundColor={
                                activeTab === IFSType.IFSProposed ? "#1e66ab" : "#f0f0f0"
                            }
                            sx={{ color: activeTab === IFSType.IFSProposed ? "#fff" : "#1e66ab" }}
                        >
                            IFS Proposed
                        </CustomButton>
                    </Grid2>
                    <Grid2>
                        <CustomButton
                            onClick={() => handleTabClick(IFSType.IFSThirdparty)}
                            backgroundColor={
                                activeTab === IFSType.IFSThirdparty ? "#1e66ab" : "#f0f0f0"
                            }
                            sx={{ color: activeTab === IFSType.IFSThirdparty ? "#fff" : "#1e66ab" }}
                        >
                            IFS Third Party
                        </CustomButton>
                    </Grid2>
                    <Grid2>
                        <CustomButton
                            onClick={() => handleTabClick(IFSType.Graph)}
                            backgroundColor={activeTab === IFSType.Graph ? "#1e66ab" : "#f0f0f0"}
                            sx={{ color: activeTab === IFSType.Graph ? "#fff" : "#1e66ab" }}
                        >
                            IFS Total Breakdown
                        </CustomButton>
                    </Grid2>
                    <Grid2>
                        <CustomButton
                            onClick={() => handleTabClick(IFSType.ChangePeriod)}
                            backgroundColor={
                                activeTab === IFSType.ChangePeriod ? "#1e66ab" : "#f0f0f0"
                            }
                            sx={{ color: activeTab === IFSType.ChangePeriod ? "#fff" : "#1e66ab" }}
                        >
                            Change Period
                        </CustomButton>
                    </Grid2>

                    <Grid2>
                        <CustomButton
                            onClick={() => handleTabClick(IFSType.IFSHistory)}
                            backgroundColor={
                                activeTab === IFSType.IFSHistory ? "#1e66ab" : "#f0f0f0"
                            }
                            sx={{ color: activeTab === IFSType.IFSHistory ? "#fff" : "#1e66ab" }}
                        >
                            IFS History
                        </CustomButton>
                    </Grid2>
                </Grid2>

                <Paper elevation={0} sx={{ border: '1px solid #e0eaf6', borderRadius: '14px', overflow: 'hidden', flexShrink: 0, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.5, backgroundColor: '#f0f6ff', borderBottom: '1px solid #e0eaf6' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FilterListOutlined sx={{ fontSize: 18, color: '#1e66ab' }} />
                            <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1e66ab', fontFamily: 'Poppins' }}>
                                Filters
                            </Typography>
                        </Box>
                    </Box>

                    <Grid2 sx={{
                        flexWrap: "wrap",
                        gap: 1.5,
                        display: "flex",
                        flexDirection: "column",
                        padding: '12px 20px 12px 20px',
                        backgroundColor: '#f7faff'
                    }} >
                        {/* Filters */}
                        <Grid2 size={{ xs: 12 }}>
                            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.8px', fontFamily: 'Poppins', }}>
                                Search & Filter
                            </Typography>
                        </Grid2>
                        {activeTab === IFSType.IFSHistory && (
                            <Grid2 container spacing={2} alignItems="center" >

                                {/* Project Multi Select */}
                                <Grid2>
                                    <CustomMultiSelectDropdown
                                        label="Filter by Project"
                                        selectedValues={selectedProjects}
                                        onChange={(values) => {
                                            setSelectedProjects(values);
                                            setSelectedWeek("-1"); // reset week when project changes
                                            setSelectedType("-1");
                                        }}
                                        options={filters.projects.map((p: any) => ({
                                            value: p.projectId,
                                            text: <EllipsisText text={p.projectName} />,
                                        }))}
                                        sx={{ width: DEFAULT_DROPDOWN_WIDTH }}
                                        singleVisibleChipMode={DEFAULT_Single_DROPDOWN}
                                        enableSelectAll={true}
                                    />
                                </Grid2>

                                {/* Week Dropdown (enabled only after project selection) */}
                                <Grid2>
                                    <CustomDropdown
                                        label="Filter by Week"
                                        value={selectedWeek}
                                        onChange={(e) => setSelectedWeek(e.target.value)}
                                        options={historyWeeks.map(w => ({
                                            value: w.weekId,
                                            text: w.weekLabel
                                        }))}
                                        disable={selectedProjects.length === 0}
                                        disableSorting={true}
                                        sx={{ width: DEFAULT_DROPDOWN_WIDTH, color: selectedWeek ? "#000" : "#9e9e9e", backgroundColor: "#fff" }}

                                    />
                                </Grid2>

                                <Grid2>
                                    <CustomDropdown
                                        label="Filter by Type"
                                        value={selectedType}
                                        onChange={(e) => setSelectedType(e.target.value)}
                                        options={[
                                            { value: IFSHistoryType.Definite, text: "Definite" },
                                            { value: IFSHistoryType.Proposed, text: "Proposed" },
                                        ]}
                                        disable={selectedProjects.length === 0 || selectedWeek === "-1"}
                                        sx={{ width: DEFAULT_DROPDOWN_WIDTH, color: selectedType ? "#000" : "#9e9e9e", backgroundColor: '#fff' }}
                                    />
                                </Grid2>

                                {/* Compare Button */}
                                <Grid2>
                                    <CustomButton
                                        capsule
                                        sx={{ backgroundColor: "#1e66ab", color: "white" }}
                                        onClick={handleCompare}
                                        disabled={(isCompareClicked || !isHistorySearchEnabled || selectedProjects.length == 0 || tableData.length == 0)}
                                    >
                                        Compare
                                    </CustomButton>
                                </Grid2>

                                {/* Search Button */}
                                <Grid2>
                                    <CustomButton
                                        capsule
                                        sx={{ backgroundColor: "#1e66ab", color: "white" }}
                                        onClick={handleSearch}
                                        disabled={!isHistorySearchEnabled}
                                    >
                                        <SearchIcon /> Search
                                    </CustomButton>
                                </Grid2>

                            </Grid2>
                        )}

                        {/* ---------- Filters ---------- */}

                        {(activeTab === IFSType.Graph || activeTab === IFSType.ChangePeriod) && (
                            // 🔹 Show only year dropdown in Graph tab
                            <Grid2 container spacing={2} alignItems="center" sx={{}}>
                                <Grid2>
                                    <CustomDropdownNumber
                                        label="Financial Year"
                                        value={financialYear}
                                        //onChange={(e) => setYear(Number(e.target.value))}
                                        onChange={(e) => setFinancialYear(Number(e.target.value))}
                                        // options={years.map((y) => ({
                                        //   value: y,
                                        //   text: y.toString(),
                                        // }))}
                                        onClick={() => {
                                            setGraphFilterMode("FY");   // 👈 activate FY on click
                                            setMonthRange("");          // clear month range
                                        }}
                                        options={financialYearOptions}
                                        disable={
                                            activeTab === IFSType.Graph
                                                ? graphFilterMode === "MONTH"
                                                : false
                                        }
                                        sx={{
                                            width: 240,
                                            backgroundColor: "#fff",
                                        }}
                                    />
                                </Grid2>
                                {/* Month range (optional) */}
                                <Grid2>
                                    <CustomDropdownNumber
                                        label="Select Month Range"
                                        value={monthRange}
                                        onChange={(e) =>
                                            setMonthRange(e.target.value ? Number(e.target.value) : "")
                                        }
                                        onClick={() => {
                                            setGraphFilterMode("MONTH");   // 👈 activate Month Range on click
                                        }}
                                        options={[
                                            { value: 3, text: "3 Months" },
                                            { value: 4, text: "4 Months" },
                                            { value: 6, text: "6 Months" },
                                            { value: 12, text: "12 Months" },
                                        ]}
                                        disableSorting={true}
                                        disable={
                                            activeTab === IFSType.Graph
                                                ? graphFilterMode === "FY"
                                                : false
                                        }
                                        sx={{
                                            width: 200,
                                            backgroundColor: "#fff",
                                            color: graphFilterMode === "MONTH" ? "#000" : "#9e9e9e",
                                        }}
                                    />
                                </Grid2>
                                {/* <Grid2>
              <CustomButton
                onClick={handleClearFilters}
                disabled={!isAnyFilterActive}
              >
                Clear All Filters
              </CustomButton>
            </Grid2> */}

                            </Grid2>
                        )}
                        <>

                            <Grid2 container spacing={2} alignItems="center" sx={{ flexWrap: "wrap" }}>
                                {/* PM */}
                                <Grid2>
                                    <Tooltip
                                        title={buildTooltip(projectManager, filters.projectManagers, "userId", "fullName")}
                                        placement="top"
                                        arrow
                                    >
                                        <Grid2>
                                            <CustomMultiSelectDropdown
                                                label="Filter by PM"
                                                selectedValues={projectManager}
                                                onChange={setProjectManager}
                                                options={filters.projectManagers.map((pm: any) => ({
                                                    value: pm.userId,
                                                    text: withTooltip(pm.fullName),
                                                }))}
                                                sx={{ width: DEFAULT_DROPDOWN_WIDTH }}
                                                singleVisibleChipMode={DEFAULT_Single_DROPDOWN}
                                            />
                                        </Grid2>
                                    </Tooltip>
                                </Grid2>

                                {/* TPL */}
                                <Grid2>
                                    <Tooltip
                                        title={buildTooltip(technicalLead, filters.technicalProjectLeads, "userId", "fullName")}
                                        placement="top"
                                        arrow
                                    >
                                        <Grid2>
                                            <CustomMultiSelectDropdown
                                                label="Filter by TPL"
                                                selectedValues={technicalLead}
                                                onChange={setTechnicalLead}
                                                options={filters.technicalProjectLeads.map((tpl: any) => ({
                                                    value: tpl.userId,
                                                    text: withTooltip(tpl.fullName),
                                                }))}
                                                sx={{ width: DEFAULT_DROPDOWN_WIDTH }}
                                                singleVisibleChipMode={DEFAULT_Single_DROPDOWN}
                                            />
                                        </Grid2>
                                    </Tooltip>
                                </Grid2>

                                {/* Fee Stage */}
                                <Grid2>
                                    <Tooltip
                                        title={buildTooltip(feeStage, filters.feeStages, "feeStageAreaNameId", "feeStageAreaName")}
                                        placement="top"
                                        arrow
                                    >
                                        <Grid2>
                                            <CustomMultiSelectDropdown
                                                label="Filter by Area"
                                                selectedValues={feeStage}
                                                onChange={setFeeStage}
                                                options={filters.feeStages.map((fs: any) => ({
                                                    value: fs.feeStageAreaNameId,
                                                    text: withTooltip(fs.feeStageAreaName),
                                                }))}
                                                sx={{ width: DEFAULT_DROPDOWN_WIDTH }}
                                                singleVisibleChipMode={DEFAULT_Single_DROPDOWN}
                                            />
                                        </Grid2>
                                    </Tooltip>
                                </Grid2>

                                {/* Client */}
                                <Grid2>
                                    <Tooltip
                                        title={buildTooltip(client, filters.clients, "clientId", "clientName")}
                                        placement="top"
                                        arrow
                                    >
                                        <Grid2>
                                            <CustomMultiSelectDropdown
                                                label="Filter by Client"
                                                selectedValues={client}
                                                onChange={setClient}
                                                options={filters.clients.map((c: any) => ({
                                                    value: c.clientId,
                                                    text: withTooltip(c.clientName),
                                                }))}
                                                sx={{ width: DEFAULT_DROPDOWN_WIDTH }}
                                                singleVisibleChipMode={DEFAULT_Single_DROPDOWN}
                                            />
                                        </Grid2>
                                    </Tooltip>
                                </Grid2>

                                {/* TOP BUTTONS */}
                                {!showMore && (
                                    <>
                                        <Grid2>
                                            <CustomButton
                                                capsule
                                                onClick={() => setShowMore(true)}
                                                backgroundColor="#f0f0f0"
                                                sx={{ color: "#1e66ab" }}
                                            >
                                                <ExpandMoreIcon /> More Filters
                                            </CustomButton>
                                        </Grid2>

                                        <Grid2>
                                            <CustomButton
                                                capsule
                                                sx={{ backgroundColor: "#1e66ab", color: "white" }}
                                                onClick={handleSearch}
                                                disabled={!isAnyFilterActive}
                                            >
                                                <SearchIcon /> Search
                                            </CustomButton>
                                        </Grid2>

                                        {activeTab !== IFSType.Graph && (
                                            <Grid2>
                                                <IconButton onClick={handleDownload}>
                                                    <DownloadIcon />
                                                </IconButton>
                                            </Grid2>
                                        )}
                                    </>
                                )}
                            </Grid2>

                            {!showMore && (
                                <Grid2 container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                                    {/* Empty grid to align under PM */}
                                    <Grid2>
                                        <CustomButton
                                            capsule
                                            onClick={handleClearFilters}
                                            disabled={!isAnyFilterActive}
                                            backgroundColor={isAnyFilterActive ? "#1e66ab" : "#f0f0f0"}
                                            sx={{ color: isAnyFilterActive ? "#fff" : "#1e66ab" }}
                                        >
                                            Clear All Filters
                                        </CustomButton>
                                    </Grid2>
                                </Grid2>
                            )}

                            {/* EXTRA FILTERS */}
                            {showMore &&
                                (
                                    <>
                                        {/* ================= ROW 2 : EXTRA FILTERS ================= */}
                                        <Grid2 container spacing={2} alignItems="center" >
                                            {/* Tags */}
                                            <Grid2>
                                                <Tooltip
                                                    title={buildTooltip(tag, filters.tags, "proposalTagId", "proposalTag")}
                                                    placement="top"
                                                    arrow
                                                >
                                                    <Grid2>
                                                        <CustomMultiSelectDropdown
                                                            label="Filter by Tags"
                                                            selectedValues={tag}
                                                            onChange={setTag}
                                                            options={filters.tags.map((t: any) => ({
                                                                value: t.proposalTagId,
                                                                text: t.proposalTag,
                                                            }))}
                                                            sx={{ width: DEFAULT_DROPDOWN_WIDTH, mt: -1 }}
                                                            singleVisibleChipMode={DEFAULT_Single_DROPDOWN}
                                                        />
                                                    </Grid2>
                                                </Tooltip>
                                            </Grid2>

                                            <Grid2>
                                                <CustomDropdown
                                                    label="Filter by AFP"
                                                    value={afp}
                                                    onChange={(e) => setAfp(e.target.value)}
                                                    options={[
                                                        { value: "1", text: "Yes" },
                                                        { value: "0", text: "No" },
                                                    ]}
                                                    sx={{ width: DEFAULT_DROPDOWN_WIDTH, backgroundColor: "#fff" }}
                                                />
                                            </Grid2>
                                            {activeTab !== IFSType.Graph && (
                                                <Grid2>
                                                    <CustomDropdown
                                                        label="Completed by India"
                                                        value={completedByIndia}
                                                        onChange={(e) => setCompletedByIndia(e.target.value)}
                                                        options={[
                                                            { value: "1", text: "Yes" },
                                                            { value: "0", text: "No" },
                                                        ]}
                                                        sx={{ width: DEFAULT_DROPDOWN_WIDTH, backgroundColor: "#fff" }}
                                                    />
                                                </Grid2>
                                            )}
                                            <Grid2>
                                                <CustomDropdown
                                                    label="Status"
                                                    value={status}
                                                    onChange={(e) => setStatus(e.target.value)}
                                                    options={[
                                                        { value: "1", text: "Active" },
                                                        { value: "0", text: "Inactive" },
                                                    ]}
                                                    sx={{ width: 250, backgroundColor: "#fff" }}
                                                />
                                            </Grid2>
                                            <Grid2>
                                                <CustomButton
                                                    capsule
                                                    onClick={() => setShowMore(false)}
                                                    backgroundColor="#f0f0f0"
                                                    sx={{ color: "#1e66ab" }}
                                                >
                                                    <ExpandLessIcon /> Less Filters
                                                </CustomButton>
                                            </Grid2>

                                            <Grid2>
                                                <CustomButton
                                                    capsule
                                                    sx={{ backgroundColor: "#1e66ab", color: "white" }}
                                                    onClick={handleSearch}
                                                    disabled={!isAnyFilterActive}
                                                >
                                                    <SearchIcon /> Search
                                                </CustomButton>
                                            </Grid2>

                                            <Grid2>
                                                <IconButton onClick={handleDownload}>
                                                    <DownloadIcon />
                                                </IconButton>
                                            </Grid2>
                                        </Grid2>

                                        <Grid2 container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                                            <Grid2>
                                                <CustomButton
                                                    capsule
                                                    onClick={handleClearFilters}
                                                    disabled={!isAnyFilterActive}
                                                    backgroundColor={isAnyFilterActive ? "#1e66ab" : "#f0f0f0"}
                                                    sx={{ color: isAnyFilterActive ? "#fff" : "#1e66ab" }}
                                                >
                                                    Clear All Filters
                                                </CustomButton>
                                            </Grid2>
                                        </Grid2>



                                    </>
                                )}

                        </>
                    </Grid2>




                </Paper>



                {/* ---------- Table / Forecast ---------- */}
                <Box
                    sx={{
                        border: "1px solid #ccc",
                        width: "100%",
                        // flex: 1,            // Take all remaining space below filters
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                    }}
                >
                    {(localLoading) && (
                        <Typography sx={{ color: "#1e66ab", textAlign: "center" }}>
                            Loading...
                        </Typography>
                    )}



                    {!localLoading && tableData.length > 0 && (activeTab === IFSType.IFSDefinite || activeTab === IFSType.IFSProposed || activeTab === IFSType.IFSThirdparty || activeTab === IFSType.IFSHistory) && (() => {
                        const validProjects = tableData.filter((p) => p.startDate && p.endDate);
                        if (validProjects.length === 0) return null;

                        const startDate = validProjects.reduce<Dayjs>((min, p) => {
                            const date = dayjs(p.startDate);
                            return date.isBefore(min) ? date : min;
                        }, dayjs(validProjects[0].startDate));

                        const endDate = validProjects.reduce<Dayjs>((max, p) => {
                            const date = dayjs(p.endDate);
                            return date.isAfter(max) ? date : max;
                        }, dayjs(validProjects[0].endDate));

                        return (
                            <CustomIFSTracker
                                ref={forecastRef}
                                forecastData={tableData}
                                activeMonthDetails={activeMonthDetails}
                                startDate={startDate.format("YYYY-MM-DD")}
                                endDate={endDate.format("YYYY-MM-DD")}
                                forecastType={
                                    activeTab === IFSType.IFSDefinite
                                        ? 1
                                        : activeTab === IFSType.IFSProposed
                                            ? 2
                                            : activeTab === IFSType.IFSThirdparty
                                                ? 3
                                                : 0 // fallback if needed
                                }
                                readOnly={false}
                                isCompareEligible={isCompareEligible}

                            />
                        );
                    })()}



                    {activeTab === IFSType.Graph && monthlySummaries.length > 0 && (
                        <Graph
                            monthlySummaries={monthlySummaries}
                            totals={graphTotals}
                        />
                    )}

                    {activeTab === IFSType.ChangePeriod && (
                        <ChangePeriodGraph
                            monthlySummaries={monthlySummaries}
                            totals={graphTotals}
                        />
                    )}


                    {/* {activeTab === IFSType.Graph && (
            <Graph projects={tableData} />
          )} */}

                    {!tableData.length &&
                        !localLoading &&
                        activeTab &&
                        (activeTab === IFSType.IFSDefinite
                            || activeTab === IFSType.IFSProposed
                            || activeTab === IFSType.IFSThirdparty
                            || activeTab === IFSType.IFSHistory) && (
                            <Typography
                                variant="body1"
                                sx={{
                                    color: '#1e66ab',

                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >


                                No data found matching the search criteria.
                            </Typography>
                        )}
                </Box>
            </Box>
        </AppLayout>
    );
};

export default IFSTracker;